/**
 * `/enter` — THE PHONE-TO-CABINET HAND-OFF LANDING, AND THE COMMON DOOR.
 *
 * THE ONE ROUTE THE CORPUS FIXES. "The app calls authors-api with its Firebase ID token -> we
 * return a short-lived, single-use hand-off code -> the app opens authors.moonx.app/enter?code=…
 * -> the SPA exchanges the code for a Firebase custom token minted only for the uid carried on that
 * verified ID token and calls signInWithCustomToken" (adr/0007-web-app-stack.md). The path and
 * the parameter name are not ours; everything else on this screen is.
 *
 * IT IS A TRANSIT SCREEN AND SHOULD SHOW ALMOST NOTHING. Four steps, in order:
 *   1. read `code` from the query;
 *   2. TAKE IT OUT OF THE ADDRESS BAR IMMEDIATELY — it is a credential, and left alone it sits in
 *      the address bar, in history, in the referrer of the next request and in any screen-share.
 *      `replaceState` also means Back cannot re-land on a spent code;
 *   3. POST it to `/v1/handoff/exchange` WITH NO CREDENTIAL OF ANY KIND — the operation carries
 *      `security: []` on purpose, because "the code IS the credential";
 *   4. `signInWithCustomToken`, then read `GET /v1/me` once and route on.
 *
 * THE THREE FAILURES ARE ONE FAILURE. Unknown, expired and already-used arrive as a single 401 with
 * `HANDOFF_CODE_INVALID` and nothing distinguishes them, so this screen renders one message and
 * does NOT invite a retry of the same code — it is dead in all three cases. See
 * ../../features/auth/components/HandoffCodeDeadPanel.tsx.
 *
 * A 503 ON THE EXCHANGE SPENDS THE CODE TOO. The server redeems the code BEFORE it mints the custom
 * token (decision-log row 223), so the exchange's 503 is a mint that failed on a code that is now
 * used: a retry of it can only land on the dead-link 401. So the screen says the service was briefly
 * unavailable and, like the dead-link panel, sends the person back to the app for a fresh link. A
 * 503 on any OTHER call (the session read after a good exchange) keeps its retry.
 *
 * AND NOBODY IS SIGNED OUT HERE, EVER. This 401 is about the code, not about a session: the
 * operation has no security scheme, so there was no session for it to reject. A person who was
 * already signed in on this origin still is.
 *
 * A DESTINATION MAY RIDE ALONG (R-034): `/enter?code=…&next=/articles/<id>/edit`. Only the `code`
 * leaves the address, so the router's door lands on `next` once signed in - when it names a screen
 * the cabinet answers (./landing.ts; anything else lands on the home). "Sign in here instead" keeps
 * it too, so a dead link from the phone still ends on the article it was opened for.
 */
import { useEffect, useRef, useState } from 'react';
import { Anchor, Button, EmptyState, Group, Stack, Text } from '@mantine/core';
import { ErrorState, LoadingState } from '@moonx/ui';
import { AuthShell } from '../../features/auth/components/AuthShell';
import { HandoffCodeDeadPanel } from '../../features/auth/components/HandoffCodeDeadPanel';
import { IdentityUnavailablePanel } from '../../features/auth/components/IdentityUnavailablePanel';
import { readHandoffCode, HANDOFF_CODE_PARAM } from '../../features/auth/handoff/enter-code';
import type { HandoffCodeReading } from '../../features/auth/handoff/enter-code';
import {
  browserNavigation,
  CABINET_HOME_PATH,
  SIGN_IN_PATH,
  returnPathFrom,
  signInPathFor,
  stripQueryParameter,
} from '../../features/auth/navigation';
import type { Navigation } from '../../features/auth/navigation';
import { useAuthSession } from '../../features/auth/session/AuthSessionProvider';
import type { EnterOutcome } from '../../features/auth/session/AuthSessionProvider';
import { Trans, useTranslation } from '../../i18n';

export interface EnterRouteProps {
  readonly onSignedIn?: () => void;
  readonly navigation?: Navigation;
}

type Stage =
  | { readonly kind: 'reading' }
  | { readonly kind: 'exchanging' }
  | { readonly kind: 'no-code' }
  | { readonly kind: 'truncated'; readonly length: number }
  | { readonly kind: 'done'; readonly outcome: EnterOutcome };

export function EnterRoute({ onSignedIn, navigation = browserNavigation }: EnterRouteProps) {
  const { t } = useTranslation('auth');
  const { state, enterWithCode, secondsUntilRetry, retryNow } = useAuthSession();
  const [stage, setStage] = useState<Stage>({ kind: 'reading' });
  const [retryCount, setRetryCount] = useState(0);

  /**
   * The code, captured at mount and then removed from the URL. Held in a ref rather than in state
   * because it is a credential: it must not end up in a React DevTools tree or in an error
   * boundary's serialised props.
   */
  const capturedCode = useRef<string | null>(null);
  /** StrictMode double-invokes effects; a hand-off code is single use, so this must not run twice. */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const reading: HandoffCodeReading = readHandoffCode(navigation.currentSearch());

    // STEP 2, BEFORE ANYTHING ELSE. Even a malformed code leaves the address bar: it is still
    // somebody's secret, and it is still in the history entry.
    if (reading.kind !== 'absent') {
      navigation.replace(
        stripQueryParameter(
          navigation.currentPath(),
          navigation.currentSearch(),
          HANDOFF_CODE_PARAM,
        ),
      );
    }

    if (reading.kind === 'absent') {
      setStage({ kind: 'no-code' });
      return;
    }
    if (reading.kind === 'malformed') {
      setStage({ kind: 'truncated', length: reading.length });
      return;
    }

    capturedCode.current = reading.code;
    setStage({ kind: 'exchanging' });
  }, [navigation]);

  const code = capturedCode.current;
  useEffect(() => {
    if (stage.kind !== 'exchanging' || code === null) {
      return;
    }
    let cancelled = false;
    void enterWithCode(code).then((outcome) => {
      if (!cancelled) {
        setStage({ kind: 'done', outcome });
      }
    });
    return () => {
      cancelled = true;
    };
    // `retryCount` is in the dependency list on purpose: bumping it re-runs the exchange, which is
    // the only retry this screen offers — after a 429, and after `unavailable` (see that branch).
  }, [stage.kind, code, enterWithCode, retryCount]);

  const signedIn = stage.kind === 'done' && stage.outcome.kind === 'signed-in';
  const sessionReady = state.phase === 'ready';

  useEffect(() => {
    if (signedIn && sessionReady && onSignedIn !== undefined) {
      onSignedIn();
    }
  }, [signedIn, sessionReady, onSignedIn]);

  const goToSignIn = (): void => {
    // The carried `next`, if any, goes with the person; `/sign-in`'s door filters it the same way.
    navigation.push(signInPathFor(returnPathFrom(navigation.currentSearch())));
  };

  if (stage.kind === 'reading' || stage.kind === 'exchanging') {
    return (
      <AuthShell title={t('opening.title')}>
        <LoadingState label={t('opening.loadingLabel')} lines={2} />
      </AuthShell>
    );
  }

  if (stage.kind === 'no-code') {
    // AN EMPTY STATE THAT SAYS NOTHING IS A BUG. This one says why it is empty, what belongs here,
    // and offers exactly one action.
    return (
      <AuthShell title={t('enter.noCode.title')}>
        <EmptyState title={t('enter.noCode.heading')} description={t('enter.noCode.description')}>
          <EmptyState.Actions>
            <Button variant="filled" onClick={goToSignIn}>
              {t('common.signInHereInstead')}
            </Button>
          </EmptyState.Actions>
        </EmptyState>
      </AuthShell>
    );
  }

  if (stage.kind === 'truncated') {
    return (
      <AuthShell title={t('enter.truncated.title')}>
        <ErrorState
          title={t('enter.truncated.heading')}
          secondaryAction={
            <Button variant="filled" size="sm" onClick={goToSignIn}>
              {t('common.signInHereInstead')}
            </Button>
          }
        >
          <Text size="sm">{t('enter.truncated.body')}</Text>
        </ErrorState>
      </AuthShell>
    );
  }

  const { outcome } = stage;

  if (outcome.kind === 'code-dead' || outcome.kind === 'malformed') {
    // ONE PANEL FOR BOTH, because from here they are the same thing to the person: this link
    // cannot be used and a fresh one is a tap away. The distinction 400-vs-401 is for our logs.
    return (
      <AuthShell title={t('enter.cannotBeUsed')}>
        <HandoffCodeDeadPanel onSignInInstead={goToSignIn} />
      </AuthShell>
    );
  }

  if (outcome.kind === 'rate-limited') {
    return (
      <AuthShell title={t('enter.rateLimited.title')}>
        <ErrorState
          title={t('enter.rateLimited.heading')}
          onRetry={() => {
            setRetryCount((n) => n + 1);
            setStage({ kind: 'exchanging' });
          }}
          retryLabel={t('enter.rateLimited.retry')}
          secondaryAction={
            <Button variant="subtle" size="sm" onClick={goToSignIn}>
              {t('common.signInHereInstead')}
            </Button>
          }
        >
          <Stack gap="xs">
            <Text size="sm">
              {t('enter.rateLimited.body', { count: outcome.retryAfterSeconds })}
            </Text>
            {/*
              THE WAIT IS THE PLATFORM'S NUMBER WHEN IT SENDS ONE. Every 429 on authors-api
              $refs the shared `Problem429`, which requires an integer `Retry-After` since
              decision-log row 220 (row 170: every 429 sends one); `parseRetryAfterSeconds`
              reads it and falls back to its own default only when the header is missing or
              unreadable. No `X-RateLimit-*` is declared.
            */}
            <Text size="sm" c="dimmed">
              {t('enter.rateLimited.notUsedUp')}
            </Text>
          </Stack>
        </ErrorState>
      </AuthShell>
    );
  }

  if (outcome.kind === 'unavailable-code-spent') {
    // THE EXCHANGE'S 503 (decision-log row 223). The code was redeemed before the mint failed, so
    // it is used up: NO retry of it is offered — the only thing it could reach is the dead-link
    // 401. The way back is the dead-link panel's (a fresh link from the app, or a sign-in here),
    // and the words say why this happened, because the person did nothing wrong and their account
    // is fine. Every exchange 503 lands here, a proxy's included: the status is what this app
    // branches on (failure.ts), and a fresh link works whichever of the two it was.
    return (
      <AuthShell title={t('enter.cannotBeUsed')}>
        <ErrorState
          title={t('enter.codeSpent.heading')}
          secondaryAction={
            <Button variant="filled" size="sm" onClick={goToSignIn}>
              {t('common.signInHereInstead')}
            </Button>
          }
        >
          <Stack gap="xs">
            <Text size="sm">{t('enter.codeSpent.body')}</Text>
            <Text size="sm" c="dimmed">
              {t('enter.codeSpent.notYourAccount')}
            </Text>
          </Stack>
        </ErrorState>
      </AuthShell>
    );
  }

  if (outcome.kind === 'unavailable') {
    // An undeclared 5xx, or no network. Neither says the request redeemed the code, so retrying
    // the SAME code is offered here and nowhere else. The exchange's own 503 never reaches this
    // branch: behind it the code is already spent (the branch above).
    return (
      <AuthShell title={t('common.oneMoment')}>
        <Stack gap="md">
          <IdentityUnavailablePanel
            secondsUntilRetry={secondsUntilRetry}
            onRetryNow={() => {
              retryNow();
              setRetryCount((n) => n + 1);
              setStage({ kind: 'exchanging' });
            }}
          />
          <Text size="sm" c="dimmed">
            <Trans
              t={t}
              i18nKey="enter.unavailable.keepsHappening"
              components={{
                anchor: (
                  <Anchor
                    href={SIGN_IN_PATH}
                    size="sm"
                    onClick={(event) => {
                      event.preventDefault();
                      goToSignIn();
                    }}
                  />
                ),
              }}
            />
          </Text>
        </Stack>
      </AuthShell>
    );
  }

  if (outcome.kind === 'sign-in-failed') {
    return (
      <AuthShell title={t('enter.signInFailed.title')}>
        <ErrorState
          title={t('enter.signInFailed.heading')}
          secondaryAction={
            <Button variant="filled" size="sm" onClick={goToSignIn}>
              {t('common.signInHereInstead')}
            </Button>
          }
        >
          <Text size="sm">{t('enter.signInFailed.body')}</Text>
        </ErrorState>
      </AuthShell>
    );
  }

  // Signed in. The session read may still be in flight, or reconnecting, or refused — all of which
  // the sign-in screen already renders, so this screen only has to say what it did and get out of
  // the way.
  if (state.phase === 'reconnecting') {
    return (
      <AuthShell title={t('common.signedInTitle')}>
        <IdentityUnavailablePanel
          secondsUntilRetry={secondsUntilRetry}
          onRetryNow={retryNow}
          sessionHeld={state.session !== null}
        />
      </AuthShell>
    );
  }

  if (!sessionReady) {
    return (
      <AuthShell title={t('common.signedInTitle')} standfirst={t('opening.checking')}>
        <LoadingState label={t('opening.loadingLabel')} lines={3} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('common.signedInTitle')}>
      <Stack gap="md">
        <Text size="sm">
          {state.identity?.displayName === null || state.identity?.displayName === undefined
            ? t('enter.welcomeBack')
            : t('enter.welcomeBackNamed', { name: state.identity.displayName })}
        </Text>
        <Group>
          <Button
            variant="filled"
            onClick={() => {
              if (onSignedIn !== undefined) {
                onSignedIn();
                return;
              }
              navigation.replace(CABINET_HOME_PATH);
            }}
          >
            {t('common.continueToCabinet')}
          </Button>
        </Group>
      </Stack>
    </AuthShell>
  );
}

/*
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): every state above is drawn in
 * the doors' card over the stars (`AuthShell`), and in plain words - the screen no longer prints
 * its own address ("/enter") or how many characters a cut-short code had; the person needs neither
 * to get a fresh link. Written down here because the header above is cited by its line numbers.
 */
